import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildAlgerianInvoiceHtml } from '../../utils/receiptDownload';

// Store branding → Invoice: the store's legal details for the Algerian
// invoice (saved inside its invoice template) and a live preview of the real
// invoice built from them with sample data.

const FIELDS = [
  { key: 'legalName', labelKey: 'invoiceLegal.legalName', placeholder: 'SARL Al Balsam Optique' },
  { key: 'nif', labelKey: 'invoiceLegal.nif', placeholder: '000123456789012' },
  { key: 'nis', labelKey: 'invoiceLegal.nis', placeholder: '000116012345678' },
  { key: 'agrementDds', labelKey: 'invoiceLegal.agrementDds', placeholder: '123/DSP/2024' },
  { key: 'email', labelKey: 'invoiceLegal.email', placeholder: 'contact@example.dz' },
];

const SAMPLE_ORDER = {
  orderNumber: 'ORD-26-10-19-00-05-008',
  invoiceNumber: '05/0001/2026',
  total: 19500,
  payment: { amount: 9500, method: 'cash' },
  visitor: { firstName: 'Chadlia', lastName: 'Rebiai', phone: '0663849634', birthday: '1964-04-04' },
  prescription: { prescriptionType: 'single_vision', rightEyeFar: { sphere: 0.25 }, leftEyeFar: { sphere: 0.5 } },
  items: [
    { type: 'frame', name: 'GOZLER 7050', quantity: 1, unitPrice: 9500, totalPrice: 9500 },
    { type: 'lens', name: 'NET Safe blue', quantity: 2, unitPrice: 5000, totalPrice: 10000 },
  ],
};

const InvoiceLegalPanel = ({ config, onChange, branding, address }) => {
  const { t } = useTranslation();
  const upd = (field, val) => onChange({ ...config, [field]: val });

  const previewHtml = useMemo(() => buildAlgerianInvoiceHtml({
    orderData: { ...SAMPLE_ORDER, invoiceIssuedAt: new Date().toISOString(), store: { name: branding.storeName, address } },
    branding: { ...branding, invoiceTemplate: config },
  }), [config, branding, address]);

  const input = { width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.7rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.85rem', fontFamily: 'inherit', background: '#fff' };
  const label = { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.25rem' };
  const tva = config.tvaRate === undefined || config.tvaRate === null || config.tvaRate === '' ? 19 : config.tvaRate;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 340px) minmax(0, 1fr)', gap: '1.25rem', alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>{t('invoiceLegal.hint')}</div>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label style={label} htmlFor={`inv-${f.key}`}>{t(f.labelKey)}</label>
            <input id={`inv-${f.key}`} style={input} value={config[f.key] || ''} placeholder={f.placeholder}
              onChange={(e) => upd(f.key, e.target.value)} />
          </div>
        ))}
        <div>
          <label style={label} htmlFor="inv-tva">{t('invoiceLegal.tvaRate')}</label>
          <select id="inv-tva" style={{ ...input, cursor: 'pointer' }} value={String(tva)}
            onChange={(e) => upd('tvaRate', Number(e.target.value))}>
            <option value="19">19 %</option>
            <option value="9">9 %</option>
            <option value="0">{t('invoiceLegal.noTva')}</option>
          </select>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.25rem' }}>{t('invoiceLegal.tvaHint')}</div>
        </div>
      </div>
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
          {t('invoiceLegal.preview')}
        </div>
        <iframe title={t('invoiceLegal.preview')} srcDoc={previewHtml} sandbox=""
          style={{ width: '100%', height: 760, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }} />
      </div>
    </div>
  );
};

export default InvoiceLegalPanel;
